import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  Paper,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { aiService, AIStatus } from '../services/aiService';

const AiSettings: React.FC = () => {
  const [aiStatus, setAIStatus] = useState<AIStatus | null>(null);
  const [aiLoading, setAILoading] = useState(false);

  const loadAIStatus = async () => {
    try {
      setAILoading(true);
      const data = await aiService.getStatus();
      setAIStatus(data.aiStatus);
    } catch {
      toast.error('Failed to load AI status');
    } finally {
      setAILoading(false);
    }
  };

  useEffect(() => {
    loadAIStatus();
  }, []);

  const handleAIPause = async () => {
    try {
      setAILoading(true);
      const response = await aiService.pause();
      setAIStatus(response.aiStatus);
      toast.success('AI processing paused');
    } catch {
      toast.error('Failed to pause AI processing');
    } finally {
      setAILoading(false);
    }
  };

  const handleAIResume = async () => {
    try {
      setAILoading(true);
      const response = await aiService.resume();
      setAIStatus(response.aiStatus);
      toast.success('AI processing resumed');
    } catch {
      toast.error('Failed to resume AI processing');
    } finally {
      setAILoading(false);
    }
  };

  const handleOpenAIFallbackToggle = async (enabled: boolean) => {
    try {
      setAILoading(true);
      const response = await aiService.setOpenAIFallback(enabled);
      setAIStatus(response.aiStatus);
      toast.success(`OpenAI fallback ${enabled ? 'enabled' : 'disabled'}`);
    } catch {
      toast.error('Failed to update OpenAI fallback setting');
    } finally {
      setAILoading(false);
    }
  };

  if (!aiStatus && aiLoading) {
    return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>AI Controls</Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>AI Processing Control</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Control AI usage for analyzing WhatsApp messages. Pause AI to save costs or resume for automatic ride qualification.
            </Typography>

            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Chip
                label={aiStatus?.enabled ? 'AI Enabled' : 'AI Paused'}
                color={aiStatus?.enabled ? 'success' : 'warning'}
                icon={aiStatus?.enabled ? <PlayIcon /> : <PauseIcon />}
              />
              {aiStatus && (
                <Typography variant="body2" color="text.secondary">
                  Queue: {aiStatus.queueSize} messages
                </Typography>
              )}
            </Box>

            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                color={aiStatus?.enabled ? 'warning' : 'success'}
                startIcon={aiStatus?.enabled ? <PauseIcon /> : <PlayIcon />}
                onClick={aiStatus?.enabled ? handleAIPause : handleAIResume}
                disabled={aiLoading}
              >
                {aiLoading ? 'Processing...' : aiStatus?.enabled ? 'Pause AI' : 'Resume AI'}
              </Button>

              <Button
                variant="outlined"
                onClick={loadAIStatus}
                disabled={aiLoading}
              >
                Refresh Status
              </Button>
            </Box>

            <Box mt={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(aiStatus?.openAIFallbackEnabled)}
                    onChange={(e) => handleOpenAIFallbackToggle(e.target.checked)}
                    disabled={aiLoading || !aiStatus?.enabled}
                  />
                }
                label="Enable OpenAI fallback if Ollama fails"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Primary path is Ollama. When this is ON and Ollama fails, backend tries OpenAI. When OFF, backend skips OpenAI and uses rule-based fallback.
              </Typography>
            </Box>

            {aiStatus && !aiStatus.enabled && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                AI processing is paused. WhatsApp messages will be queued but not analyzed until you resume AI processing.
              </Alert>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>AI Usage Information</Typography>

            <Typography variant="body2" paragraph>
              <strong>When AI is enabled:</strong>
              <br />
              • WhatsApp messages are analyzed with Ollama first
              <br />
              • If Ollama fails and OpenAI fallback is enabled, OpenAI is used
              <br />
              • Ride details are extracted and stored as neutral leads
              <br />
              • Qualified rides are automatically created in the system
            </Typography>

            <Typography variant="body2" paragraph>
              <strong>When AI is paused:</strong>
              <br />
              • Messages are still received and queued
              <br />
              • No AI analysis occurs (saves costs)
              <br />
              • Manual processing required for ride qualification
            </Typography>

            <Typography variant="body2" color="text.secondary">
              <strong>Tip:</strong> Pause AI during low-activity periods to reduce costs, then resume when needed.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AiSettings;
